// Mirrors the Go backend's application/boat_dto.go
export type BoatStatus = 'available' | 'sold' | 'in_service' | 'in_storage'

// Where the boat sits in the sales pipeline. Independent of operational status.
export type ListingState = 'for_sale' | 'sold' | 'off_market'

// New vs. pre-owned inventory. Mirrors the Go backend's domain.BoatCondition.
export type BoatCondition = 'new' | 'used'

// Hull/style category. Drives which add-on catalog items fit the boat
// (mirrors the boat_type enum in migration 000035 and the Go BoatType).
export type BoatType =
  | 'bowrider'
  | 'pontoon'
  | 'center_console'
  | 'fishing'
  | 'wake_surf'
  | 'cruiser'
  | 'deck_boat'
  | 'pwc'
  | 'sailboat'
  | 'other'

export const BOAT_TYPES: BoatType[] = [
  'bowrider',
  'pontoon',
  'center_console',
  'fishing',
  'wake_surf',
  'cruiser',
  'deck_boat',
  'pwc',
  'sailboat',
  'other',
]

export const BOAT_TYPE_LABELS: Record<BoatType, string> = {
  bowrider: 'Bowrider',
  pontoon: 'Pontoon',
  center_console: 'Center Console',
  fishing: 'Fishing',
  wake_surf: 'Wake / Surf',
  cruiser: 'Cruiser',
  deck_boat: 'Deck Boat',
  pwc: 'PWC',
  sailboat: 'Sailboat',
  other: 'Other',
}

export const BOAT_STATUS_LABELS: Record<BoatStatus, string> = {
  available: 'Available',
  sold: 'Sold',
  in_service: 'In Service',
  in_storage: 'In Storage',
}

export const LISTING_STATE_LABELS: Record<ListingState, string> = {
  for_sale: 'For Sale',
  sold: 'Sold',
  off_market: 'Off Market',
}

// Open (active/sold/paid_out) consignment agreement on a boat, from the
// BoatDTO's embedded brief. Absent when the boat has no open agreement.
export interface BoatConsignmentBrief {
  id: string
  status: 'active' | 'sold' | 'paid_out'
  consignor_id: string
}

// Floor-plan loan lifecycle + curtailment cadence. Mirror the loan_status /
// curtailment_frequency Postgres enums (migration 000028) and the Go
// domain.LoanStatus / CurtailmentFrequency typed-strings.
export type LoanStatus = 'active' | 'paid_off'
export type CurtailmentFrequency = 'monthly' | 'quarterly'

export const LOAN_STATUS_LABELS: Record<LoanStatus, string> = {
  active: 'Active',
  paid_off: 'Paid Off',
}

export const CURTAILMENT_FREQUENCY_LABELS: Record<CurtailmentFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
}

export const CURTAILMENT_FREQUENCY_SHORT: Record<CurtailmentFrequency, string> = {
  monthly: 'mo',
  quarterly: 'qtr',
}

// Active floor-plan loan on a boat, from the BoatDTO's embedded brief.
// Absent when the boat is unencumbered.
export interface BoatLoanBrief {
  id: string
  lender: string
  principal: number
  curtailment_amount: number
  interest_rate: number // annual percentage (8.25 = 8.25% APR); 0 = not tracked
  frequency: CurtailmentFrequency
  status: LoanStatus
  next_due_on?: string // ISO date of the earliest unpaid curtailment
}

export interface Boat {
  id: string
  make: string
  model: string
  year: number
  price: number // sales/list price shown to buyers
  cost: number // dealer's acquisition cost (0 when unknown)
  length_ft: number
  status: BoatStatus
  listing_state: ListingState
  condition: BoatCondition
  type: BoatType // hull/style category (drives add-on fit)
  color: string // free-text hull color ("" when unset)
  description: string // free-text listing description ("" when unset)
  engine_manufacturer: string // enginedata manufacturer key ("" when unknown)
  engine_model: string
  engine_count: number // number of engines (0 = unpowered/unrecorded)
  engine_serial: string // serial(s) of the primary engine ("" when unknown)
  engine_hours?: number | null // latest hour-meter reading
  hin: string
  customer_id?: string | null
  sold_at?: string | null // ISO string; set when the boat entered the sold state
  consignment?: BoatConsignmentBrief // open agreement, absent = none
  loan?: BoatLoanBrief // active floor-plan loan, absent = none
  cover_image_url?: string // /media/... path; absent when no cover is set
  cover_thumb_url?: string // same image, ~480px ingest-generated variant
  created_at: string // ISO string
  updated_at: string // ISO string
}

// One row of a loan's curtailment payment schedule. Mirrors the Go
// application/boat_loan_dto.go LoanCurtailmentDTO.
export interface LoanCurtailment {
  id: string
  sequence: number // 1-based
  due_on: string // YYYY-MM-DD
  amount: number
  paid_on?: string | null // YYYY-MM-DD; absent = unpaid
}

// One row of a loan's interest schedule — the cost-of-money twin of
// LoanCurtailment, due the same date as the sequence-matched curtailment.
// Mirrors the Go LoanInterestChargeDTO.
export interface LoanInterestCharge {
  id: string
  sequence: number // 1-based, pairs with the curtailment of the same sequence
  due_on: string // YYYY-MM-DD
  amount: number
  paid_on?: string | null // YYYY-MM-DD; absent = unpaid
}

// Full floor-plan loan with both schedules + rollups. Mirrors the Go
// application/boat_loan_dto.go BoatLoanDTO.
export interface BoatLoan {
  id: string
  boat_id: string
  lender: string
  principal: number
  curtailment_amount: number
  interest_rate: number // annual percentage; 0 = not tracked
  frequency: CurtailmentFrequency
  first_curtailment_on: string // YYYY-MM-DD
  status: LoanStatus
  paid_off_on?: string | null // YYYY-MM-DD
  payoff_amount?: number | null // lender's quoted payoff, for reference
  notes: string
  curtailments: LoanCurtailment[]
  interest_charges: LoanInterestCharge[]
  paid_total: number // sum of paid curtailments (principal reduction)
  accrued_unpaid_total: number // unpaid and due on/before today
  accrued_unpaid_count: number
  interest_paid_total: number // sum of paid interest charges (cost of money)
  next_due_on?: string | null // earliest unpaid row (curtailment or interest)
  created_at: string // ISO string
  updated_at: string // ISO string
}

// Fields a client may send when creating or updating a floor-plan loan.
export interface BoatLoanWriteBody {
  id?: string // client-generated for offline create replay idempotency
  lender: string
  principal: number
  curtailment_amount: number
  interest_rate?: number // annual percentage; 0 = not tracked
  frequency?: CurtailmentFrequency
  first_curtailment_on: string // YYYY-MM-DD
  notes?: string
}

// Mirrors the Go backend's application/boat_image_dto.go
export type BoatImageCategory = 'exterior' | 'interior' | 'motor' | 'trailer'

// Display metadata for the category picker (order = display order).
export const BOAT_IMAGE_CATEGORIES: { value: BoatImageCategory; label: string }[] = [
  { value: 'exterior', label: 'Exterior' },
  { value: 'interior', label: 'Interior' },
  { value: 'motor', label: 'Motor' },
  { value: 'trailer', label: 'Trailer' },
]

export interface BoatImage {
  id: string
  boat_id: string
  url: string // /media/... path
  thumb_url: string // same image, ~480px ingest-generated variant
  content_type: string
  size_bytes: number
  title: string // "" when untitled
  category: BoatImageCategory | '' // "" = uncategorized
  is_cover: boolean
  sort_order: number
  created_at: string // ISO string
}

// Fields a client may send when creating or updating a boat.
export interface BoatWriteBody {
  id?: string // client-generated for offline create replay idempotency
  make: string
  model: string
  year: number
  price: number
  cost?: number
  length_ft: number
  status?: BoatStatus
  listing_state?: ListingState
  condition?: BoatCondition
  type?: BoatType
  color?: string
  description?: string
  engine_manufacturer?: string
  engine_model?: string
  engine_count?: number
  engine_serial?: string
  engine_hours?: number | null
  hin?: string
  customer_id?: string
  // Adopt an intake inspection: its record and photos move onto the new boat
  // (only inspections not attached to a work order are eligible).
  inspection_id?: string
}
